import { Injectable, OnDestroy } from '@angular/core';

/**
 * Service for managing call-related timers
 * Handles duration tracking, post-call timers, cleanup timers, etc.
 */
@Injectable({
  providedIn: 'root'
})
export class CallTimerService implements OnDestroy {
  // Timer storage
  private callTimers = new Map<string, NodeJS.Timeout>();
  private postCallTimer: NodeJS.Timeout | null = null;
  private cleanupTimer: NodeJS.Timeout | null = null;
  private healthCheckTimer: NodeJS.Timeout | null = null;

  /**
   * Start call duration timer
   * @param sessionId - Call session ID
   * @param callback - Function to call each second with duration
   */
  startCallTimer(sessionId: string, callback: (duration: number) => void): void {
    this.stopCallTimer(sessionId);

    const startTime = Date.now();
    const timer = setInterval(() => {
      const duration = Math.floor((Date.now() - startTime) / 1000);
      callback(duration);
    }, 1000);

    this.callTimers.set(sessionId, timer);
  }

  /**
   * Stop call duration timer
   * @param sessionId - Call session ID
   */
  stopCallTimer(sessionId: string): void {
    const timer = this.callTimers.get(sessionId);
    if (timer) {
      clearInterval(timer);
      this.callTimers.delete(sessionId);
    }
  }

  /**
   * Start post-call timer (transitions to idle after delay)
   * @param timeout - Milliseconds before callback executes
   * @param callback - Function to call when timer expires
   */
  startPostCallTimer(timeout: number, callback: () => void): void {
    this.clearPostCallTimer();

    this.postCallTimer = setTimeout(() => {
      callback();
      this.postCallTimer = null;
    }, timeout);
  }

  /**
   * Clear post-call timer
   */
  clearPostCallTimer(): void {
    if (this.postCallTimer) {
      clearTimeout(this.postCallTimer);
      this.postCallTimer = null;
    }
  }

  /**
   * Start cleanup timer (force cleanup after timeout)
   * @param timeout - Milliseconds before callback executes
   * @param callback - Function to call when timer expires
   */
  startCleanupTimer(timeout: number, callback: () => void): void {
    this.clearCleanupTimer();

    this.cleanupTimer = setTimeout(() => {
      callback();
      this.cleanupTimer = null;
    }, timeout);
  }

  /**
   * Clear cleanup timer
   */
  clearCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Start health check timer (periodic health checks)
   * @param interval - Milliseconds between checks
   * @param callback - Function to call on each interval
   */
  startHealthCheckTimer(interval: number, callback: () => void): void {
    this.clearHealthCheckTimer();

    const runHealthCheck = () => {
      callback();
      this.healthCheckTimer = setTimeout(runHealthCheck, interval);
    };

    runHealthCheck();
  }

  /**
   * Clear health check timer
   */
  clearHealthCheckTimer(): void {
    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  /**
   * Clear all timers
   */
  clearAll(): void {
    // Clear all call timers
    this.callTimers.forEach((timer) => clearInterval(timer));
    this.callTimers.clear();

    // Clear system timers
    this.clearPostCallTimer();
    this.clearCleanupTimer();
    this.clearHealthCheckTimer();
  }

  /**
   * Get active timer count (for debugging)
   */
  getActiveTimerCount(): number {
    let count = this.callTimers.size;
    if (this.postCallTimer) count++;
    if (this.cleanupTimer) count++;
    if (this.healthCheckTimer) count++;
    return count;
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.clearAll();
  }
}
